import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import API_URL from "../components/config";
import { setAuthToken } from '../components/utils/auth';
import Bubble from "../components/admin/Bubbles";
import { Lock, ArrowRight } from "lucide-react";

export default function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${API_URL}/login`, { email, password });
      if (data && data.token) {
        setAuthToken(data.token);
        navigate("/choosenone");
      } else {
        setError("Login failed - no token received");
      }
    } catch (error) {
      if (error.response?.data?.message) {
        setError(error.response.data.message);
      } else {
        setError("Login failed - please try again");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen flex items-center justify-center bg-black overflow-hidden">
      {/* Bubbles */}
      <Bubble x="20%" y="80%" size="80px" delay={0} />
      <Bubble x="60%" y="70%" size="60px" delay={1} />
      <Bubble x="30%" y="90%" size="100px" delay={2} />
      <Bubble x="80%" y="85%" size="50px" delay={1.5} />
      <Bubble x="10%" y="75%" size="70px" delay={2.5} />

      {/* Sophisticated Background Pattern */}
      <div className="absolute inset-0 opacity-5 pointer-events-none">
        <div className="w-full h-full" style={{
          backgroundImage: `radial-gradient(circle at 50px 50px, white 2px, transparent 2px)`,
          backgroundSize: '100px 100px'
        }}></div>
      </div>

      {/* Gradient Overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-black via-gray-900 to-black pointer-events-none"></div>

      {/* Login Card */}
      <form
        onSubmit={login}
        className="relative z-10 w-full max-w-md p-6 sm:p-8 md:p-10 bg-white/5 backdrop-blur-2xl border border-white/20 rounded-3xl shadow-xl space-y-8"
      >
        <div className="flex flex-col items-center mb-6">
          <span className="bg-white/10 p-3 rounded-full mb-4">
            <Lock size={32} className="text-white" />
          </span>
          <h2 className="text-2xl am:text-3xl font-thin text-white text-center tracking-widest mb-2">
            Executive <span className="text-transparent bg-clip-text bg-gradient-to-r from-white to-white/60">Access</span>
          </h2>
          <p className="text-white/60 text-sm font-light tracking-wide text-center">
            Secure portal for authorized users only
          </p>
        </div>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded text-center">
            {error}
          </div>
        )}

        <div>
          <label className="block mb-2 text-white/70 text-sm font-light tracking-wide">Work Email </label>
          <input
            type="email"
            className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/40 focus:outline-none focus:border-white/40 focus:bg-white/15 transition-all duration-300"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="officialmail@company.com"
            required
            disabled={loading}
          />
        </div>

        <div>
          <label className="block mb-2 text-white/70 text-sm font-light tracking-wide">Password</label>
          <input
            type="password"
            className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/40 focus:outline-none focus:border-white/40 focus:bg-white/15 transition-all duration-300"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••"
            required
            disabled={loading}
          />
        </div>

        <button
          type="submit"
          disabled={loading}
          className="group w-full bg-white text-black font-medium py-4 rounded-xl text-sm tracking-[0.1em] uppercase hover:bg-white/90 transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-50"
        >
          {loading ? "Logging in..." : (
            <>
              Login
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </>
          )}
        </button>
      </form>
    </div>
  );
}
